import React, { useCallback, useEffect, useState } from "react";
import {
  BackHandler,
  FlatList,
  View,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import SearchIcon from "@assets/svg/SearchIcon";
import { debounce } from "lodash";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import { useFocusEffect } from "@react-navigation/native";
import color from "@constants/color";
import Text from "@components/CustomText";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import {
  getReconByDbId,
  markItemLostDamaged,
  markReconSubmit,
  submitReconCollectedCash,
} from "@services/api";
import CashCollection from "./components/CashCollection";
import ItemCollection from "./components/ItemCollection";
import ReconTabBtn from "./components/ReconTabBtn";
import ReconError from "./components/ReconError";
import ReconUserCard from "./components/ReconUserCard";
import CashCollectionModal from "./components/CashCollectionModal";
import BottomButton from "./components/BottomButton";

const tabEnum = {
  PENDING: "pending",
  RTO: "rto",
};

const ReconDetails = ({ navigation, route }) => {
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cashCollectionModalVisible, setCashCollectionModalVisible] =
    useState(false);
  const [reconData, setReconData] = useState({});
  const [selectedTab, setSelectedTab] = useState(tabEnum.RTO);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentExpandedIndex, setCurrentExpandedIndex] = useState(null);
  const { item } = route.params;
  useFocusEffect(
    useCallback(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]),
  );
  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };
    const backHandlerSub = BackHandler.addEventListener("hardwareBackPress", backHandler);
    return () => {
      backHandlerSub.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };
  const fetchData = async ({ searchTerm = "", isSearch = true } = {}) => {
    try {
      setIsLoading(true);
      const { data } = await getReconByDbId({
        db_id: item?.db_id,
        search_term: isSearch ? searchTerm || searchText : "",
      });

      setReconData(data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitReconCollectedCash = async (amount) => {
    try {
      setIsLoading(true);
      const payload = {
        ofd_date: reconData?.ofdDate,
        amount,
        totalCollectedFromCx: reconData?.totalCollectedFromCx,
      };
      await submitReconCollectedCash({
        db_id: item?.db_id,
        payload,
      });
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitReconItemMissing = async (data, value) => {
    try {
      setIsLoading(true);
      const payload = {
        ofd_date: reconData?.ofdDate,
        lost_damaged: value,
        order_item_id: data?.id,
        actual_quantity: data?.quantity,
        prev_missing_quantity: data?.missing_qty,
        source: "RECON",
        cp_db_id: item?.db_id,
      };

      await markItemLostDamaged(payload);
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSearchInputChange = debounce((text) => {
    setSearchText(text);
    fetchData({ searchTerm: text, isSearch: text.length > 0 });
  }, 500);

  const setReconTab = (tab) => {
    setSelectedTab(tab);
  };
  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
    setIsRefreshing(false);
  };
  const renderheader = () => {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: color.white,
          padding: 16,
          paddingVertical: 0,
          paddingBottom: 16,
          marginBottom: 8,
        }}
      >
        <CashCollection
          totalCollectedFromCx={reconData?.totalCollectedFromCx}
          onPress={() => setCashCollectionModalVisible(true)}
          totalCollectedFromDb={reconData?.totalCollectedFromDb}
          onPressSubmit={onSubmitReconCollectedCash}
          reconEditingEnabled={reconData?.reconEditingEnabled}
        />
        <ItemCollection
          totalItemCollectionAmount={reconData?.totalItemCollectionAmount}
          totalItemCollectionQty={reconData?.totalItemCollectionQty}
        />
        <ReconTabBtn
          pending={reconData?.openItems?.length}
          rto={reconData?.returnedItems?.length}
          selectedTab={selectedTab}
          setSelectedTab={setReconTab}
        />
        <ReconError
          text="Lost/Damage marked by DB after Out For Delivery: "
          value={reconData?.totalIssuesMarked}
        />
        {selectedTab === tabEnum.RTO ? (
          <Text
            variant="caption_2"
            style={{ marginTop: 8, textAlign: "center", color: color.black06 }}
          >
            These are
            <Text
              variant="caption_1"
              style={{
                color: color.black06,
                lineHeight: 16,
              }}
            >
              {` RTO items. `}
            </Text>
            Keep these items ready for pickup by CityMall tomorrow.
          </Text>
        ) : (
          <Text
            variant="caption_2"
            style={{ marginTop: 8, textAlign: "center", color: color.black06 }}
          >
            These are
            <Text
              variant="caption_1"
              style={{
                color: color.black06,
                lineHeight: 16,
              }}
            >
              {` Pending items. `}
            </Text>
            Keep these items in bag for tomorrow delivery.
          </Text>
        )}
      </View>
    );
  };
  const callCustomer = (phoneNumber) => {
    handleCallButtonPress(phoneNumber);
  };

  // eslint-disable-next-line no-shadow
  const renderItem = ({ item, index }) => {
    return (
      <ReconUserCard
        item={item}
        callCustomer={callCustomer}
        onPressMissingQty={onSubmitReconItemMissing}
        setCurrentExpandedIndex={setCurrentExpandedIndex}
        index={index}
        expandedIndex={currentExpandedIndex}
      />
    );
  };
  const onPressBottomButton = async () => {
    setIsLoading(true);
    try {
      const payload = {
        ofd_date: reconData?.ofdDate,
      };
      await markReconSubmit({
        db_id: item?.db_id,
        payload,
      });
      navigation.pop();
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <HeaderWithSearch
        headertitle={item?.db_name || "Delivery Boy"}
        headerColor="#fff"
        hideShadow={true}
        navigation={navigation}
        route={route}
        searchPlaceholder="Search customer, route, items, bag no."
        isSearchEnabled={isSearchEnabled}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        searchIcon={<SearchIcon height={24} width={24} />}
        onSearchInputChange={onSearchInputChange}
        onPressCrossIcon={() => onSearchInputChange("")}
        onPressSearchIcon={() => setIsSearchEnabled(true)}
      />
      {isLoading ? (
        <ActivityIndicator
          size="large"
          color={color.primary}
          style={styles.loading}
        />
      ) : (
        <>
          <FlatList
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            refreshing={isRefreshing}
            initialScrollIndex={currentExpandedIndex}
            onRefresh={onRefresh}
            data={
              selectedTab === tabEnum.RTO
                ? reconData?.returnedItems
                : reconData?.openItems
            }
            keyExtractor={(i) => i.user_id}
            renderItem={renderItem}
            ListHeaderComponent={isSearchEnabled ? null : renderheader}
            ListFooterComponent={<View style={{ height: 120 }} />}
          />
          <BottomButton
            title="Continue"
            onPress={onPressBottomButton}
            totalMissingAmount={reconData?.totalMissingAmount}
            totalMissingQty={reconData?.totalMissingQty}
          />
          <CashCollectionModal
            showModal={cashCollectionModalVisible}
            setShowModal={setCashCollectionModalVisible}
            totalCollectedFromCxSummary={reconData?.totalCollectedFromCxSummary}
          />
        </>
      )}
    </View>
  );
};

export default ReconDetails;

const styles = StyleSheet.create({
  loading: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
});
